import React from 'react';
import { SwipeModal } from '@hqo/react-components-library/dist/molecules/modals/swipe-modal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';
import { ModalContainer } from 'components/paymentIFrame/styles';
import { useStepHeader } from 'pages/booking-add-ons/step-header.context';
import { Container, StepContent, StepModal, StepTitle } from './styles';

interface SwipeModalContentProps {
  content: JSX.Element;
  onPressBack?: VoidFunction;
  onClose: VoidFunction;
  withLeftArrowButton?: boolean;
  isFullscreen?: boolean;
  // RES-999: de-modal booking details. Callers pass this for the receipt content specifically
  // (checkout keeps its existing SwipeModal behavior unchanged) — see payment-content.tsx.
  disableSwipeModal?: boolean;
  /*
   * Opt-in, and only the add-on step passes it. The default branch is a bare div, so content renders
   * INLINE in whichever host PaymentContent was mounted in — invisible for the other routes because
   * theirs bring their own modal, and a TRAP for this one, which does not: see where it is read.
   *
   * ⚠️ Not "desktop" in the sense of a viewport, despite the name. `StepModal` goes full width below
   * `PAYMENT_CONTENT_WIDTH`, so it is the small-screen surface too whenever the swipe modal declines.
   */
  withDesktopModal?: boolean;
}

export const ReceiptBooking = ({
  content,
  onPressBack,
  withLeftArrowButton = false,
  onClose,
  isFullscreen = false,
  disableSwipeModal = false,
  withDesktopModal = false,
}: SwipeModalContentProps) => {
  const { showMiniappPaymentsNavigation } = useFlags();
  const isMobileDevice = useIsSmallViewportWidth();

  /*
   * RES-1336 — a multi-step child describes its own header, so one wrapper can serve every step with
   * the right control instead of one fixed guess for all of them. Empty on all eight other routes
   * (nothing provides it), so their props and behaviour are untouched — see the context's own note.
   */
  const header = useStepHeader();
  /* The step's target wins when it has one; otherwise the caller's, which is the pre-existing control. */
  const backTarget = header.onBack ?? onPressBack;
  /*
   * ⛔ WHETHER TO DRAW A BACK CONTROL IS NOT `Boolean(backTarget)`. `backTarget` carries the caller's
   * fallback, and both call sites always pass `onPressBack` — so that test is permanently true and
   * every step got an arrow, including the terminal ones with nothing above them. The question is
   * whether THIS STEP declared a back, or the caller asked for one; `withLeftArrowButton` on this
   * surface means "leave the cancel-booking sub-step".
   */
  const wantsBackControl = Boolean(header.onBack) || withLeftArrowButton;
  /*
   * A step whose content renders the title as its own heading gets the dialog NAMED but the header
   * band left empty — see `titleOwnedByContent`.
   */
  const visibleTitle = header.titleOwnedByContent ? undefined : header.title;
  const closeTarget = header.onClose ?? onClose;
  /*
   * ⭐ "THIS CONTENT WANTS A MODAL SURFACE" — one question, two renderings: the swipe sheet on a
   * phone, `StepModal` on a desktop. Only the add-on steps declare it; every other route through
   * this wrapper brings its own modal and renders inline here.
   *
   * ⛔ IT IS NOT GATED ON VIEWPORT, AND GATING IT STRANDS PHONES. Below both branches is a bare
   * container with NO back and NO exit, and on this route that is a trap rather than a plain layout:
   * `PaymentContent` calls `header.hideHeader()` and `swipe.disableBackSwipe()` for the same
   * `isMobileDevice` and the same path, so the host's own way back is gone too. Caught in review on
   * #1936 after a viewport gate was added here — and the gate fixed nothing it claimed to, since
   * `StepModal` already carries `width: 100%` below `PAYMENT_CONTENT_WIDTH`.
   */
  const wantsModalSurface = header.withDesktopModal ?? withDesktopModal;

  /*
   * ⛔ THE STEPS INHERIT `disableSwipeModal` DELIBERATELY. It is RES-999's (#1869, `60cfbc75`) —
   * "de-modal booking details → routed page + kill swipe-modal" — and `PaymentContent` passes it as
   * `enable2WayChatRedesignResourceBooking` into the ONE wrapper that serves both its `receipt` and
   * `add-on-request` children. So when that flag is on, the add-on steps lose the sheet too. That is
   * the intended direction, not blast radius: confirmed with Mason, who is newly in the LD segment.
   *
   * ⚠️ Which makes `wantsModalSurface` below load-bearing on phones rather than desktop-only. With
   * the sheet off, `StepModal` is the surface a phone gets — it goes `width: 100%` below
   * `PAYMENT_CONTENT_WIDTH` — and it is the only thing carrying a back control, since
   * `PaymentContent` also calls `header.hideHeader()` and `swipe.disableBackSwipe()` on this route.
   */
  const isSwipeModalContent: boolean = showMiniappPaymentsNavigation && isMobileDevice && !disableSwipeModal;

  const buildContent = (): JSX.Element => (
    <ModalContainer isSwipeModalContent={isSwipeModalContent} isFullscreen={isFullscreen}>
      {content}
    </ModalContainer>
  );

  if (isSwipeModalContent) {
    return (
      <SwipeModal
        reverseAnimation={false}
        content={buildContent()}
        title={visibleTitle}
        onPressBack={backTarget}
        onClose={closeTarget}
        withLeftArrowButton={wantsBackControl}
        withLeftExitButton={!wantsBackControl}
      />
    );
  }

  /* StepContent rather than buildContent(): ModalContainer's fixed 528px left a dead band. */
  if (wantsModalSurface) {
    return (
      <StepModal
        visible
        /* Names the dialog; ModalWrapper renders it nowhere, which is what `headerElement` is for. */
        title={header.title}
        headerElement={visibleTitle ? <StepTitle>{visibleTitle}</StepTitle> : undefined}
        /* A back arrow when this step has somewhere to go back TO, the exit X when not. */
        withBackButton={wantsBackControl}
        /*
         * "chevron", not "arrow": both render the same ArrowLeftChevron, but only this one carries
         * role, tabIndex and a key handler. The label stays in the DOM — it is the control's only
         * accessible name — and StepModal clips it so this reads as a bare arrow.
         */
        backButtonVariant="chevron"
        onPressBack={backTarget}
        withExitButton={!wantsBackControl}
        onClose={closeTarget}
        content={<StepContent data-testid="add-on-step-content">{content}</StepContent>}
      />
    );
  }

  return <Container isFullscreen={isFullscreen}>{buildContent()}</Container>;
};
